/**
 * Загрузка документов о долгах в таблицу кредиторов (решения от 25.09.2026):
 * кредитные отчёты «Скоринг бюро», ОКБ и НБКИ, постановления ФССП по исполнительному
 * производству. Можно выбрать несколько файлов сразу.
 *
 * Сервер разбирает PDF и возвращает ПРЕДЛОЖЕНИЕ строк. В дело попадает
 * только то, что сотрудник отметил: какие долги включать, решает юрист.
 *
 * ДВОЙНОЙ УЧЁТ. Один и тот же долг бывает и в кредитном отчёте,
 * и в постановлении ФССП — если кредитор взыскивал его через суд. Дважды
 * включённый долг в заявлении в суд — ошибка по существу. Такой долг
 * включается по кредитному отчёту (ответ юриста 27.09.2026): строка
 * постановления приходит без отметки. Прочие повторы — кредитор уже есть
 * в деле — тоже без отметки и с пояснением: включить их — осознанное решение.
 *
 * Для сверки показывается и то, на кого документ: если ИНН не совпадает
 * с должником дела, это видно сразу.
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { creditorCoreKey, newRowId, type Creditor, type ObligatoryPayment } from '@bd/domain';
import { money } from '@bd/doc-engine/money';
import { api, type CreditorsImport } from '../../../api.js';
import { DocumentUpload } from './DocumentUpload.js';
import type { ImportHandle, Preloaded } from './import-handle.js';

type Row = CreditorsImport['rows'][number];

const DUPLICATE_NOTE = 'У этого кредитора уже есть строка — проверьте, не тот же ли это долг';
const FSSP_DUPLICATE_NOTE = 'Этот кредитор есть в кредитном отчёте — долг включается по отчёту, строка постановления не отмечена';
const SAME_PROCEEDING_NOTE = 'Это исполнительное производство уже есть в другом загруженном постановлении';
const OTHER_BUREAU_NOTE = (title: string) => `Этот кредитор есть в отчёте «${title}» с бо́льшим или более свежим долгом — включается по нему`;

/** Дата отчёта из заголовка: «Кредитный отчёт НБКИ от 03.09.2026» → «2026-09-03». */
const reportDate = (title: string) => /от (\d{2})\.(\d{2})\.(\d{4})/.exec(title)?.slice(1).reverse().join('-') ?? '';

/**
 * Строки, чей кредитор уже встречается в деле или в другом документе.
 *
 * • Один долг в кредитном отчёте и в постановлении ФССП включается по отчёту
 *   (ответ юриста 27.09.2026, вопрос 6): строка постановления не отмечена.
 * • Кредитор в отчётах двух бюро — по тому отчёту, где долг по нему больше,
 *   а при равенстве — где дата свежее (ответ юриста 28.09.2026). Строки
 *   прочих отчётов по этому кредитору не отмечены.
 * • Постановления одного взыскателя по РАЗНЫМ производствам — разные долги
 *   (семь штрафов ГИБДД — семь строк); по одному производству бывает
 *   несколько постановлений, и тогда отмечено первое.
 * • Кредитор уже есть в деле — без отметки: решает сотрудник.
 */
function possibleDuplicates(results: CreditorsImport[], creditors: Creditor[]): Map<string, string> {
  const inCase = new Set(creditors.map((c) => creditorCoreKey(c.name)).filter(Boolean));
  const isFssp = (r: number) => results[r]!.kind === 'fssp-initiation';
  const rows = results.flatMap((result, r) =>
    result.rows.map((row, i) => ({
      r,
      i,
      key: creditorCoreKey(row.creditor.name),
      uid: row.creditor.sourceUid ?? null,
      amount: row.creditor.amountDebt,
    })),
  );

  /** Отчёт, по которому включается кредитор: наибольший долг, затем самая свежая дата. */
  const winner = (key: string): number => {
    const reports = [...new Set(rows.filter((row) => row.key === key && !isFssp(row.r)).map((row) => row.r))];
    const sum = (r: number) => rows.filter((row) => row.key === key && row.r === r).reduce((total, row) => total + row.amount, 0);
    return reports.sort((a, b) => sum(b) - sum(a) || reportDate(results[b]!.title).localeCompare(reportDate(results[a]!.title)))[0]!;
  };

  const flagged = new Map<string, string>();
  for (const row of rows) {
    if (!row.key) continue;
    const id = `${row.r}:${row.i}`;
    if (inCase.has(row.key)) {
      flagged.set(id, DUPLICATE_NOTE);
      continue;
    }
    // Два кредита одного банка в одном отчёте — обычное дело, не повтор
    const others = rows.filter((other) => other.key === row.key && other.r !== row.r);
    if (!others.length) continue;
    const inReports = others.some((other) => !isFssp(other.r));

    if (isFssp(row.r)) {
      if (inReports) flagged.set(id, FSSP_DUPLICATE_NOTE);
      else if (others.some((other) => other.uid === row.uid && other.r < row.r)) flagged.set(id, SAME_PROCEEDING_NOTE);
    } else if (inReports) {
      const best = winner(row.key);
      if (best !== row.r) flagged.set(id, OTHER_BUREAU_NOTE(results[best]!.title));
    }
  }
  return flagged;
}

export const CreditorDocumentsImport = forwardRef<
  ImportHandle,
  {
    caseId: string;
    creditors: Creditor[];
    debtorInn: string;
    readOnly: boolean;
    onAccept: (rows: Creditor[]) => void;
    /** Штрафы и налоги по постановлениям ФССП — в «Обязательные платежи» (ответ юриста 28.09.2026) */
    onObligatory: (payments: ObligatoryPayment[]) => void;
    /** Встроенный режим (import-handle.ts): документы уже разобраны, кнопки — общие */
    preloaded?: Preloaded<CreditorsImport>;
  }
>(function CreditorDocumentsImport({ caseId, creditors, debtorInn, readOnly, onAccept, onObligatory, preloaded }, ref) {
  const embedded = Boolean(preloaded);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [results, setResults] = useState<CreditorsImport[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [duplicates, setDuplicates] = useState<Map<string, string>>(new Map());
  const [accepted, setAccepted] = useState<string | null>(null);

  const load = async (files: File[]) => {
    setBusy(true);
    setErrors([]);
    setResults([]);
    setAccepted(null);
    const uids = creditors.map((c) => c.sourceUid).filter((uid): uid is string => Boolean(uid));
    const loaded: CreditorsImport[] = [];
    const failed: string[] = [];

    // По одному: сервер разбирает отчёт в 250 листов несколько секунд,
    // и десяток файлов разом ему незачем
    for (const file of files) {
      try {
        loaded.push(await api.importCreditorsDocument(caseId, file, uids));
      } catch (e) {
        failed.push(`${file.name}: ${(e as Error).message}`);
      }
    }
    ingest(loaded, failed);
  };

  /** Разобранные документы → строки с отметками и пометками о повторах. */
  const ingest = (loaded: CreditorsImport[], failed: string[]) => {
    const flagged = possibleDuplicates(loaded, creditors);
    const all = loaded.flatMap((result, r) => result.rows.map((_, i) => `${r}:${i}`));
    setResults(loaded);
    setDuplicates(flagged);
    setSelected(new Set(all.filter((key) => !flagged.has(key))));
    setErrors(failed);
    setBusy(false);
  };

  const toggle = (key: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** Все строки документа разом — для галочки в заголовке таблицы */
  const toggleDocument = (r: number, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      results[r]!.rows.forEach((_, i) => (on ? next.add(`${r}:${i}`) : next.delete(`${r}:${i}`)));
      return next;
    });

  const chosenRows = results.flatMap((result, r) => result.rows.filter((_, i) => selected.has(`${r}:${i}`)));
  const chosen = chosenRows.map((row) => row.creditor);

  const accept = () => {
    const toCreditors = chosenRows.filter((row) => !row.obligatory).map((row) => row.creditor);
    const payments: ObligatoryPayment[] = chosenRows
      .filter((row) => row.obligatory)
      .map((row) => ({
        id: newRowId(),
        name: row.obligatory!.name,
        arrears: row.creditor.amountDebt,
        isBusiness: false,
        confirmingDocument: `Постановление ФССП, ${row.creditor.basisDetails}`,
      }));
    if (toCreditors.length) onAccept(toCreditors);
    if (payments.length) onObligatory(payments);
    const withoutAddress = toCreditors.filter((c) => !c.address).length;
    const withoutType = toCreditors.filter((c) => !c.obligationType).length;
    const withoutAmount = toCreditors.filter((c) => c.amountDebt <= 0).length;
    const summary = [
      `Добавлено строк: ${toCreditors.length}.`,
      payments.length ? `В «Обязательные платежи» — ${payments.length} (штрафы и налоги по постановлениям).` : '',
      withoutAmount ? `У ${withoutAmount} не указана сумма — впишите её (алименты — по расчёту пристава).` : '',
      withoutAddress
        ? `У ${withoutAddress} нет адреса кредитора — впишите его; после сборки документов адрес сохранится в справочник.`
        : '',
      withoutType ? `У ${withoutType} не указано содержание обязательства — заполните по постановлению.` : '',
    ]
      .filter(Boolean)
      .join(' ');
    setAccepted(summary);
    setResults([]);
    return summary;
  };

  // Встроенный режим: документы приходят разобранными, внесение — общей кнопкой
  useEffect(() => {
    if (preloaded) ingest(preloaded.results, preloaded.errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- разбор приходит один раз
  }, [preloaded]);
  useImperativeHandle(ref, () => ({
    accept: () => (chosen.length ? accept() : null),
  }));

  const debtor = debtorInn.replace(/\s/g, '');
  const total = chosen.reduce((sum, c) => sum + c.amountDebt, 0);

  return (
    <div className="report-import">
      {!embedded && (
        <DocumentUpload
          label="Загрузить документы о долгах"
          busyLabel="Читаю документы…"
          hint="Кредитные отчёты «Скоринг бюро», ОКБ и НБКИ, постановления ФССП. Можно несколько файлов сразу. Файлы нигде не сохраняются."
          busy={busy}
          disabled={readOnly}
          onFiles={(files) => void load(files)}
        />
      )}

      {errors.map((error, i) => (
        <p key={i} className="error-text">
          {error}
        </p>
      ))}
      {accepted && <p className="hint">{accepted}</p>}

      {results.length > 0 && (
        <div className="draft">
          {results.map((result, r) => (
            <div key={r} className="import-document">
              <p>
                <strong>{result.title}</strong>
                {result.subject && (
                  <>
                    {' '}
                    — {result.subject.fullName}
                    {result.subject.inn && `, ИНН ${result.subject.inn}`}
                  </>
                )}
              </p>

              {result.subject?.inn && debtor && result.subject.inn !== debtor && (
                <p className="error-text">ИНН в документе не совпадает с ИНН должника в деле. Проверьте, тот ли документ загружен.</p>
              )}
              {result.totalsMatch === false && (
                <p className="warning">
                  Сумма найденных строк не сходится со строкой «Итого» отчёта — часть договоров могла не распознаться. Сверьте таблицу с
                  отчётом.
                </p>
              )}

              {result.rows.length === 0 && result.skipped.length === 0 ? (
                // Ни строк, ни пропущенных — разбор не нашёл в документе ни одного договора.
                // «Новых долгов нет» здесь было бы неправдой: скорее всего, не справился разбор
                <p className="warning">
                  В документе не найдено ни одного договора. Если в отчёте они есть, разбор с ним не справился: внесите долги вручную и
                  передайте файл разработчику.
                </p>
              ) : result.rows.length === 0 ? (
                <p className="hint">Новых долгов в документе нет: договоры закрыты или уже внесены в дело (см. ниже).</p>
              ) : (
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={result.rows.every((_, i) => selected.has(`${r}:${i}`))}
                          onChange={(e) => toggleDocument(r, e.target.checked)}
                          aria-label="Отметить все строки документа"
                          title="Отметить все / снять все"
                        />
                      </th>
                      <th>Кредитор</th>
                      <th>Содержание</th>
                      <th>Задолженность, ₽</th>
                      <th>Просрочка</th>
                      <th>Адрес</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row: Row, i) => {
                      const key = `${r}:${i}`;
                      const notes = [...(duplicates.has(key) ? [duplicates.get(key)!] : []), ...(row.notes ?? [])];
                      return (
                        <tr key={row.creditor.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selected.has(key)}
                              onChange={() => toggle(key)}
                              aria-label={`Добавить ${row.creditor.name}`}
                            />
                          </td>
                          <td>
                            {row.creditor.name}
                            {row.creditor.name !== row.reportName && <div className="hint">в документе: {row.reportName}</div>}
                            {notes.map((note) => (
                              <div key={note} className="warning">
                                {note}
                              </div>
                            ))}
                          </td>
                          <td>
                            {row.creditor.obligationType || '—'}
                            <div className="hint" title={row.reportKind}>
                              {row.reportKind.length > 90 ? `${row.reportKind.slice(0, 90)}…` : row.reportKind}
                            </div>
                          </td>
                          <td style={{ textAlign: 'right' }}>{money(row.creditor.amountDebt).digitsWithKopecks}</td>
                          <td>
                            {row.overdueDays ? `${row.overdueDays} дн.` : '—'}
                            {row.creditor.overdueOverThreeMonths && <div className="hint">более 3 мес.</div>}
                          </td>
                          <td>
                            {row.matched ? (
                              <span className="saved">из справочника</span>
                            ) : row.creditor.address ? (
                              <span className="saved">из документа</span>
                            ) : (
                              <span className="warning">вписать</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Долг обнулился после просрочки — на виду: возможно, продан коллектору */}
              {result.skipped
                .filter((s) => s.warning)
                .map((s, i) => (
                  <p key={i} className="warning">
                    {s.reportName}: {s.reason}
                  </p>
                ))}
              {result.skipped.some((s) => !s.warning) && (
                <details>
                  <summary className="hint">Не предлагается: {result.skipped.filter((s) => !s.warning).length}</summary>
                  <ul className="hint">
                    {result.skipped
                      .filter((s) => !s.warning)
                      .map((s, i) => (
                        <li key={i}>
                          {s.reportName} — {s.reason}
                        </li>
                      ))}
                  </ul>
                </details>
              )}
            </div>
          ))}

          {!embedded && (
            <div className="toolbar">
              <button type="button" className="primary" disabled={chosen.length === 0} onClick={accept}>
                Добавить отмеченные ({chosen.length}) на {money(total).digitsWithKopecks} ₽
              </button>
              <button type="button" className="link-button" onClick={() => setResults([])}>
                Отмена
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
